/* eslint-disable react/no-unknown-property */
import { Canvas } from '@react-three/fiber';
import * as THREE from 'three';
import {
  OrbitControls,
  PerspectiveCamera,
  useContextBridge,
  useProgress,
} from '@react-three/drei';
import { Suspense, useContext, useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import Style from './style.module.scss';
import Marker from 'componentsForThree/Marker';
import { ControlContext } from 'provider/ControlProvider';
import Popup from 'components/Popup';
import controlBtn from 'assets/img/back.png';
import logo from 'assets/img/pro2.png';
import MarkerParent from 'componentsForThree/MarkerParent';
import { PuffLoader } from 'react-spinners';
import Model from 'componentsForThree/ModelLoader';
import { DoubleSide } from 'three';

type MarkerInfo = {
  name: string;
  pos: Array<number>;
};
type ButtonInfo = {
  name: string;
  id: number;
  zone: number;
  img: string;
  pos: Array<number>;
};
type CameraProps = {
  pos: Array<number>;
};

const infoList = new Array<MarkerInfo>(
  {
    name: 'Hip',
    pos: [-0.007, 9.634, -1.046],
  },
  {
    name: 'Shoulder',
    pos: [-0.84, 14.701, -1.742],
  },
  {
    name: 'Spine',
    pos: [-1.027, 12.074, 0],
  },
  {
    name: 'Elbow',
    pos: [-0.663, 12.51, 3.614],
  },
  {
    name: 'Hand & Wrist',
    pos: [-0.021, 10.31, 5.737],
  },
  {
    name: 'Knee',
    pos: [-0.475, 4.6, 1.523],
  },
  {
    name: 'Ankle & Feet',
    pos: [-0.58, 0.617, 2.033],
  }
);

export default function Configurator() {
  const ContextBridge = useContextBridge(ControlContext);
  const { active, progress } = useProgress();
  const { buttonIndex, popupRef, setGender, setButtonIndex } =
    useContext(ControlContext);
  const [sindex, setIndex] = useState<number>(-1);
  const [markVisible, SetMarkVisible] = useState<boolean>(false);
  const orbit = useRef<any>();
  const camera = useRef<any>();
  const myMesh = useRef<THREE.Mesh>();
  const [drag, setDrag] = useState<boolean>(false);
  useEffect(() => {
    if (progress === 100 && !active) {
      if (drag)
        orbit.current.touches = {
          ONE: THREE.TOUCH.PAN,
          TWO: THREE.TOUCH.DOLLY_PAN,
        };
      else
        orbit.current.touches = {
          ONE: THREE.TOUCH.ROTATE,
          TWO: THREE.TOUCH.DOLLY_PAN,
        };
    }
  }, [drag]);
  const cameraMove = ({ pos }: CameraProps) => {
    gsap.to(orbit.current.target, {
      duration: 1,
      ease: 'back.out(0.8)',
      x: pos[0],
      y: pos[1],
      z: pos[2],
    });
    gsap.to(orbit.current, {
      onStart: () => {
        orbit.current.minDistance = 7;
      },
      duration: 1.2,
      ease: 'power1.out',
      maxDistance: 15,
    });
    gsap.to('#resetBtn', {
      duration: 1,
      ease: 'power4.Out',
      opacity: 1,
    });
  };
  const cameraRest = () => {
    setIndex(-1);
    gsap.to(orbit.current.target, {
      duration: 1,
      ease: 'back.out(0.8)',
      x: 0,
      y: 9,
      z: 0,
    });

    gsap.to(orbit.current, {
      onStart: () => {
        orbit.current.maxDistance = 80;
      },
      duration: 1.2,
      ease: 'power1.out',
      minDistance: 50,
      onComplete: () => {
        orbit.current.minDistance = 7;
      },
    });
    // setZoneIndex(-1);
    canvaseMoveRight();
    popupInitAnim();
  };
  const canvaseMoveLeft = () => {
    if (window.innerWidth >= 1024) {
      gsap.to('#canvas', {
        duration: 1,
        ease: 'back.out(0.8)',
        x: -popupRef.current.offsetWidth + 200,
      });
      gsap.to('#resetBtn', {
        duration: 1,
        ease: 'back.out(0.8)',
        x: -200,
      });
      gsap.to('#controlBtn', {
        duration: 1,
        ease: 'back.out(0.8)',
        x: -200,
      });
    } else
      gsap.to('#canvas', {
        duration: 1,
        ease: 'back.out(0.8)',
        y: -200,
        onComplete: () => {
          gsap.to('#controlBtn', {
            duration: 1,
            ease: 'back.out(0.8)',
            y: 200,
          });
          gsap.to('#resetBtn', {
            duration: 1,
            ease: 'back.out(0.8)',
            y: -250,
          });
        },
      });
  };
  const canvaseMoveRight = () => {
    if (window.innerWidth >= 1024) {
      gsap.to('#canvas', {
        duration: 1,
        ease: 'back.out(0.8)',
        x: 0,
      });
      gsap.to('#resetBtn', {
        duration: 1,
        ease: 'back.out(0.8)',
        x: 0,
      });
      gsap.to('#controlBtn', {
        duration: 1,
        ease: 'back.out(0.8)',
        x: 0,
      });
    } else
      gsap.to('#canvas', {
        duration: 1,
        ease: 'back.out(0.8)',
        y: 0,
        onComplete: () => {
          gsap.to('#controlBtn', {
            duration: 1,
            ease: 'back.out(0.8)',
            y: 0,
          });
          gsap.to('#resetBtn', {
            duration: 1,
            ease: 'back.out(0.8)',
            y: 0,
          });
        },
      });
  };
  const markAnim = () => {
    gsap.to('#marker', {
      duration: 1,
      ease: 'power4.Out',
      opacity: 0,
    });
    gsap.to('#plusMark', {
      duration: 0.3,
      ease: 'power4.Out',
      fontSize: 1,
    });
  };
  const markInitAnim = () => {
    gsap.to('#marker', {
      duration: 1,
      ease: 'power4.Out',
      opacity: 1,
    });
    gsap.to('#plusMark', {
      duration: 0.3,
      ease: 'power4.Out',
      fontSize: 20,
    });
  };
  const popupAnim = (index: number) => {
    gsap.to('#popup', {
      onStart: () => {
        setButtonIndex(index);
      },
      duration: 1,
      ease: 'power4.Out',
      opacity: 1,
      onComplete: () => {
        canvaseMoveLeft();
      },
    });
    gsap.to('#resetBtn', {
      duration: 1,
      ease: 'power4.Out',
      opacity: 1,
    });
    gsap.fromTo(
      '#textInfo',
      { duration: 1, ease: 'power1.out', opacity: 0 },
      { duration: 1, ease: 'power1.out', opacity: 1 }
    );
  };
  const popupInitAnim = () => {
    gsap.to('#popup', {
      duration: 1,
      ease: 'power1.out',
      opacity: 0,
      onComplete: () => {
        setButtonIndex(-1);
      },
    });
    gsap.to('#resetBtn', {
      duration: 0.5,
      ease: 'power4.Out',
      opacity: 0,
    });
  };
  return (
    <div
      className={Style.wrapper}
      onPointerUp={() => {
        if (markVisible) {
          SetMarkVisible(false);
          markInitAnim();
          if (drag) setDrag(false);
        }
      }}
    >
      <div id='canvas' className='relative w-screen h-screen'>
        <Suspense fallback={<Loader />}>
          <Canvas
            dpr={[1, 2]}
            onPointerDown={() => {
              if (!markVisible) {
                SetMarkVisible(true);
                markAnim();
              }
            }}
          >
            <ContextBridge>
              <ambientLight intensity={0.4} />
              <Model />
              {infoList.map((com, index) => {
                return (
                  <Marker
                    key={index}
                    name={com.name}
                    position={com.pos}
                    status
                    openStatu={sindex === index}
                    oclusionObj={myMesh as any}
                    colorIndex={1}
                    setButtonAction={(e) => {
                      cameraMove({ pos: com.pos });
                      setIndex(index);
                      e.preventDefault();
                      e.stopPropagation();
                      e.nativeEvent.stopImmediatePropagation();
                      popupAnim(index);
                    }}
                  />
                );
              })}

              <mesh
                scale={[2, 15, 2.8]}
                position={[-0.2, 10, 0]}
                onPointerDown={() => {
                  if (!drag) setDrag(true);
                }}
                visible={false}
              >
                <boxGeometry />
                <meshPhongMaterial color='royalblue' transparent opacity={0} />
              </mesh>
              <PerspectiveCamera
                makeDefault
                ref={camera}
                position={[50, 0, 0]}
                zoom={window.innerWidth > 640 ? 1.9 : 1}
                fov={40}
              />
              <OrbitControls
                ref={orbit}
                target={[0, 9, 0]}
                enableRotate={!drag}
                minDistance={7}
                maxDistance={120}
                minPolarAngle={0}
                maxPolarAngle={Math.PI / 1.75}
                touches={{
                  ONE: THREE.TOUCH.ROTATE,
                  TWO: THREE.TOUCH.DOLLY_PAN,
                }}
              />
            </ContextBridge>
          </Canvas>
        </Suspense>
        <img
          id={'controlBtn'}
          className='absolute top-6 right-6 md:top-16 md:right-16 w-16 h-16 md:w-20 md:h-20 select-none'
          src={logo}
        />
        <button
          id='resetBtn'
          className='absolute bottom-6 right-6 md:bottom-20 md:right-16 w-10 h-10 rounded-full drop-shadow-sm shadow-md bg-white opacity-0 z-50'
          onClick={cameraRest}
        >
          <img className='p-2' src={controlBtn} />
        </button>
      </div>

      <Popup />
    </div>
  );
}

function Loader() {
  return (
    <div className='text-2xl font-OS_SB p-12 text-gray-500 h-screen w-screen flex justify-center items-center text-center'>
      <div className='relative'>
        <PuffLoader color='#36d7b7' size={300} />
        <p className='absolute left-24 top-36'>Loading...</p>
      </div>
    </div>
  );
}
